import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Input, message, Modal, Row, Select, Table } from 'antd';
import { useForm } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';
import { getUserDetails, makePostCall } from '../../../../utils/helper';
import type { OrderDetails } from '../../types';

interface CriticalFindingDialogProps {
  visible: boolean;
  orderDetails: OrderDetails | null;
  onClose: () => void;
}

const CriticalFindingDialog: React.FC<CriticalFindingDialogProps> = ({ visible, orderDetails, onClose }) => {
  const [selectedPhysicians, setSelectedPhysicians] = useState<any[]>([]);
  const [diseasesList, setDiseasesList] = useState<any[]>([]);
  const [doctorsList, setDoctorsList] = useState<any[]>([]);
  const [currentDoc, setCurrentDoc] = useState<any>(null);
  const [showOtherDisease, setShowOtherDisease] = useState(false);
  const [form] = useForm();

  useEffect(() => {
    if (!visible) return;
    makePostCall('/doctors-list', {}).then(res => setDoctorsList(res?.data?.data || [])).catch(() => setDoctorsList([]));
    makePostCall('/diseases-list', {}).then(res => setDiseasesList(res?.data?.data || [])).catch(() => setDiseasesList([]));
  }, [visible]);

  const handleSubmit = () => {
    const { disease, other, findings } = form.getFieldsValue();
    makePostCall('/notify-physicians', {
      doctors: selectedPhysicians.map(d => ({ ref_cd: d.DOC_REF_CD, doc_email: d.DOC_EMAIL, doc_mobile: d.DOC_MOBILE_TEL })),
      user_id: getUserDetails()?.username,
      disease: disease === 'other' ? other : disease,
      findings,
      order_id: orderDetails?.pacs_order?.pacs_ord_id,
    })
      .then(() => { message.success('Notified'); setTimeout(onClose, 1000); })
      .catch(() => message.error('Something went wrong'));
  };

  const addDoc = () => {
    if (!currentDoc) return;
    setSelectedPhysicians(prev => [...prev, currentDoc]);
    setCurrentDoc(null);
    form.setFieldValue('ref_doc', null);
  };

  const columns = [
    { title: 'Doc Name', dataIndex: 'DOC_DISPLAY_NAME', key: 'doc_name' },
    { title: 'Doc Code', dataIndex: 'DOC_REF_CD', key: 'doc_ref_code' },
    { title: 'Doc Email', dataIndex: 'DOC_EMAIL', key: 'doc_email' },
    { title: 'Doc Phone', dataIndex: 'DOC_MOBILE_TEL', key: 'DOC_MOBILE_TEL' },
  ];

  return (
    <Modal width={800} open={visible} onCancel={onClose} okButtonProps={{ style: { display: 'none' } }}>
      <div className="critical-finding-container">
        <div className="pat-header mb-3">
          <Row>
            <Col span={10}><span className="notes-label">Name: </span><span className="notes-value">{orderDetails?.pacs_order?.patient?.pat_name}</span></Col>
            <Col span={7}><span className="notes-label">PIN: </span><span className="notes-value">{orderDetails?.pacs_order?.patient?.pat_pin}</span></Col>
            <Col span={7}><span className="notes-label">Accession: </span><span className="notes-value">{orderDetails?.pacs_order?.po_acc_no}</span></Col>
          </Row>
        </div>
        <Form form={form} onFinish={handleSubmit}>
          <Row>
            <Col>
              <Form.Item rules={[{ required: true }]} name="disease" label="Disease">
                <Select
                  allowClear showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={[{ value: 'other', label: 'Other' }, ...diseasesList.map(d => ({ label: d.disease_name, value: d.disease_code }))]}
                  style={{ width: 300 }}
                  onChange={val => setShowOtherDisease(val === 'other')}
                />
              </Form.Item>
            </Col>
            <Col>{showOtherDisease && <Form.Item rules={[{ required: true }]} name="other" label="Other"><Input /></Form.Item>}</Col>
          </Row>
          <Form.Item rules={[{ required: true }]} name="findings" label="Critical Finding"><TextArea /></Form.Item>
          <Form.Item name="ref_doc" label="Physician">
            <Row>
              <Col>
                <Select
                  style={{ width: 300 }} showSearch allowClear
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={doctorsList.map(doc => ({ label: doc.DOC_DISPLAY_NAME, value: doc.DOC_REF_CD, docObj: doc }))}
                  onChange={(_val, rec: any) => setCurrentDoc(rec?.docObj)}
                />
              </Col>
              <Col><Button disabled={!currentDoc} type="primary" onClick={addDoc}>ADD</Button></Col>
            </Row>
          </Form.Item>
          <Table columns={columns} dataSource={selectedPhysicians} />
          <Form.Item>
            <Button disabled={!selectedPhysicians.length} type="primary" style={{ width: '100%' }} htmlType="submit">SUBMIT</Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default CriticalFindingDialog;
